import React, { createContext, useContext, useMemo } from 'react'

import type { WithRenderedAt } from '@/types/next'

const RenderTimeContext = createContext<number | null>(null)

interface RenderTimeProviderProps {
  renderedAt?: string | number | null
  children: React.ReactNode
}

function normalizeRenderedAt(
  renderedAt?: string | number | null,
): number | null {
  if (renderedAt == null) return null
  if (typeof renderedAt === 'number') {
    return Number.isFinite(renderedAt) ? renderedAt : null
  }

  const parsed = new Date(renderedAt).getTime()
  return Number.isFinite(parsed) ? parsed : null
}

export const hasRenderedAt = (value: unknown): value is WithRenderedAt => {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__renderedAt' in value &&
    typeof value.__renderedAt === 'string'
  )
}

export const getPageRenderedAt = (value: unknown): string | null => {
  return hasRenderedAt(value) ? value.__renderedAt : null
}

export const RenderTimeProvider: React.FC<RenderTimeProviderProps> = ({
  renderedAt,
  children,
}) => {
  const value = useMemo(() => normalizeRenderedAt(renderedAt), [renderedAt])

  return (
    <RenderTimeContext.Provider value={value}>
      {children}
    </RenderTimeContext.Provider>
  )
}

export const useRenderTimeMs = (): number | null => {
  return useContext(RenderTimeContext)
}
